"use client";

import { useEffect, useState } from "react";
import Lottie from "lottie-react";
import type { LucideIcon } from "lucide-react";
import { useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { MotionIcon } from "@/components/motion/MotionIcon";

interface LottieIconProps {
  src: string;
  fallbackIcon: LucideIcon;
  size?: number;
  className?: string;
  pulseOnView?: boolean;
}

export function LottieIcon({
  src,
  fallbackIcon,
  size = 32,
  className,
  pulseOnView = false,
}: LottieIconProps) {
  const reduced = useReducedMotion();
  const [animationData, setAnimationData] = useState<object | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (reduced) return;

    let cancelled = false;
    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load Lottie");
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setAnimationData(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [src, reduced]);

  if (reduced || failed || !animationData) {
    return (
      <MotionIcon
        icon={fallbackIcon}
        size={size}
        className={className}
        pulseOnView={pulseOnView}
      />
    );
  }

  return (
    <span
      className={cn("inline-flex items-center justify-center", className)}
      style={{ width: size, height: size }}
    >
      <Lottie
        animationData={animationData}
        loop
        autoplay
        style={{ width: size, height: size }}
        rendererSettings={{ progressiveLoad: true }}
      />
    </span>
  );
}
